import React, { useMemo } from 'react';
import {
  useLocation,
  useNavigate,
  useParams,
} from 'react-router';


// react-router v4/v5 passed `location`, `match` and `history` props to route
// components and to components wrapped with withRouter(). The class components
// of this app still rely on these props, so this module provides them on top
// of the react-router hooks.

export const useRouterProps = () => {
  const location = useLocation();
  const params = useParams();
  const navigate = useNavigate();

  const match = useMemo(() => ({
    params,
    url: location.pathname,
    path: location.pathname,
    isExact: true,
  }), [params, location.pathname]);

  const history = useMemo(() => ({
    push: (to, state) => navigate(to, state === undefined ? undefined : { state }),
    replace: (to, state) => navigate(to, { replace: true, ...(state === undefined ? {} : { state }) }),
    go: delta => navigate(delta),
    goBack: () => navigate(-1),
    goForward: () => navigate(1),
    location,
  }), [navigate, location]);

  return { location, match, history };
};

const withRouter = (WrappedComponent) => {
  const WithRouter = (props) => {
    const routerProps = useRouterProps();
    return <WrappedComponent {...routerProps} {...props} />;
  };
  const wrappedName = WrappedComponent.displayName || WrappedComponent.name || 'Component';
  WithRouter.displayName = `withRouter(${wrappedName})`;
  WithRouter.WrappedComponent = WrappedComponent;
  return WithRouter;
};

export default withRouter;
